import { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import './BusinessListingPage.css';
import './SearchResults.css';

const BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

export default function SearchResults() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const q = searchParams.get('q') || '';
  const [query, setQuery] = useState(q);
  const [suggestions, setSuggestions] = useState([]);
  const [showSugg, setShowSugg] = useState(false);
  const [businesses, setBusinesses] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [sortBy, setSortBy] = useState('');
  const [businessType, setBusinessType] = useState('');
  const [minRating, setMinRating] = useState('');
  const suggRef = useRef();
  const debounceRef = useRef();

  // Fetch results when query or filters change
  useEffect(() => {
    if (!q.trim()) return;
    setLoading(true);
    api.get('/api/businesses', { params: { search: q, sortBy, businessType, minRating } })
      .then(res => {
        setBusinesses(res.data.data?.businesses || []);
        setTotal(res.data.data?.total || 0);
      })
      .catch(() => setBusinesses([]))
      .finally(() => setLoading(false));
  }, [q, sortBy, businessType, minRating]);

  // Suggestions on typing
  useEffect(() => {
    clearTimeout(debounceRef.current);
    if (!query.trim() || query === q) { setSuggestions([]); return; }
    debounceRef.current = setTimeout(() => {
      api.get('/api/businesses/suggestions', { params: { q: query } })
        .then(res => setSuggestions(res.data.data || []))
        .catch(() => setSuggestions([]));
    }, 300);
  }, [query]);

  // Close suggestions on outside click
  useEffect(() => {
    const handler = (e) => { if (suggRef.current && !suggRef.current.contains(e.target)) setShowSugg(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSearch = (e) => {
    e?.preventDefault();
    if (query.trim()) { setSearchParams({ q: query.trim() }); setShowSugg(false); setSuggestions([]); }
  };

  const handleSuggClick = (name) => {
    setQuery(name);
    setSearchParams({ q: name });
    setShowSugg(false);
    setSuggestions([]);
  };

  const clearFilters = () => { setSortBy(''); setBusinessType(''); setMinRating(''); };

  return (
    <div className="blp-page">
      <div className="blp-container">

        {/* Search Bar with Suggestions */}
        <form onSubmit={handleSearch} style={{ margin: '20px 0' }}>
          <div className="sr-search-wrap" ref={suggRef}>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                className="sr-input"
                placeholder="Search businesses..."
                value={query}
                onChange={e => { setQuery(e.target.value); setShowSugg(true); }}
                onFocus={() => suggestions.length && setShowSugg(true)}
                autoComplete="off"
              />
              <button type="submit" className="sr-search-btn">🔍 Search</button>
            </div>
            {showSugg && suggestions.length > 0 && (
              <div className="sr-suggestions">
                {suggestions.map((s) => (
                  <div key={s._id} className="sr-sugg-item" onMouseDown={() => handleSuggClick(s.businessName)}>
                    <span>🏢</span>
                    <div>
                      <p className="sr-sugg-name">{s.businessName}</p>
                      {(s.city || s.businessType) && (
                        <p className="sr-sugg-sub">{[s.businessType, s.city].filter(Boolean).join(' · ')}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </form>

        {/* Filters */}
        {q && (
          <div className="blp-filters">
            <select value={sortBy} onChange={e => setSortBy(e.target.value)}>
              <option value="">Sort By</option>
              <option value="rating">Top Rated</option>
              <option value="newest">Newest</option>
              <option value="popular">Most Popular</option>
            </select>
            <select value={businessType} onChange={e => setBusinessType(e.target.value)}>
              <option value="">Business Type</option>
              <option value="Trader">Trader</option>
              <option value="Manufacturer">Manufacturer</option>
              <option value="Service Provider">Service Provider</option>
              <option value="Seller">Seller</option>
            </select>
            <select value={minRating} onChange={e => setMinRating(e.target.value)}>
              <option value="">Ratings</option>
              <option value="5">5 ★</option>
              <option value="4">4★ & above</option>
              <option value="3">3★ & above</option>
            </select>
            {(sortBy || businessType || minRating) && (
              <button className="blp-clear-btn" onClick={clearFilters}>Clear Filters</button>
            )}
          </div>
        )}

        {q && (
          <p style={{ color: '#64748b', fontSize: 13, marginBottom: 12 }}>
            {loading ? 'Searching...' : `${total} result${total !== 1 ? 's' : ''} for "${q}"`}
          </p>
        )}

        {/* Results */}
        <div className="blp-list">
          {loading ? (
            <div className="blp-empty"><span>⏳</span><p>Searching businesses...</p></div>
          ) : !q.trim() ? (
            <div className="blp-empty"><span>🔍</span><p>Type something to search</p></div>
          ) : businesses.length === 0 ? (
            <div className="blp-empty"><span>😕</span><p>No businesses found for "{q}"</p></div>
          ) : (
            businesses.map(b => (
              <div key={b._id} className="blp-card">
                <div className="blp-card-body">
                  <div className="blp-card-img">
                    {b.logo ? <img src={`${BASE}/${b.logo}`} alt={b.businessName} /> : <span>🏢</span>}
                  </div>
                  <div className="blp-card-info">
                    <div className="blp-name-row">
                      <h3 onClick={() => navigate(`/business/${b._id}`)}>{b.businessName}</h3>
                      {b.verified && <span className="blp-verified">Verified Business</span>}
                    </div>
                    <p className="blp-desc">{b.businessType}{b.established ? ` | Since ${b.established}` : ''}</p>
                    <div className="blp-rating">
                      {'★'.repeat(Math.round(b.rating || 0))}{'☆'.repeat(5 - Math.round(b.rating || 0))}
                      <span>{b.reviews || 0} Reviews</span>
                    </div>
                    <p className="blp-address">📍 {b.address || b.city || ''}</p>
                    <div className="blp-actions">
                      <a href={`tel:${b.phone}`} className="blp-btn call">Call Now</a>
                      <button className="blp-btn enquiry" onClick={() => navigate(`/business/${b._id}`)}>View Details</button>
                      {b.whatsapp && <a href={`https://wa.me/${b.whatsapp}`} target="_blank" rel="noreferrer" className="blp-btn whatsapp">WhatsApp</a>}
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
